export const FACULTY_ANALYTICS_BASE = '/faculty/analytics';

/** @deprecated Legacy base — use {@link FACULTY_ANALYTICS_BASE}. Kept for redirect matching. */
export const CODING_ANALYTICS_BASE = '/faculty/coding-analytics';

/** Minimum linked accounts to show a platform overview card. */
export const PLATFORM_OVERVIEW_MIN_LINKED = 5;

/** Minimum linked accounts for charts / distribution analytics. */
export const ADVANCED_CHARTS_MIN_LINKED = 10;

/** Primary analytics navigation — faculty decision-making hierarchy. */
export const FACULTY_ANALYTICS_PRIMARY_NAV = [
  { id: 'overall', label: 'Overall Analytics', segment: 'overall' },
  { id: 'students', label: 'Student Progress', segment: 'students' },
  { id: 'batches', label: 'Assignment Analytics', segment: 'batches' },
  { id: 'effectiveness', label: 'Faculty Effectiveness', segment: 'effectiveness' },
  { id: 'platforms', label: 'Platform Analytics', segment: 'platforms' },
];

/** Platform drill-down navigation under Platform Analytics. */
export const FACULTY_PLATFORM_ANALYTICS_NAV = [
  { id: 'comparison', label: 'Compare Platforms', segment: 'comparison' },
  { id: 'performance', label: 'Batch Performance', segment: 'performance' },
  { id: 'leetcode', label: 'LeetCode', segment: 'leetcode' },
  { id: 'codeforces', label: 'Codeforces', segment: 'codeforces' },
  { id: 'geeksforgeeks', label: 'GeeksforGeeks', segment: 'geeksforgeeks' },
  { id: 'codechef', label: 'CodeChef', segment: 'codechef' },
  { id: 'contests', label: 'Contest Analytics', segment: 'contests' },
];

/** Contest Analytics drill-down (Phase 1 read model). */
export const FACULTY_CONTEST_ANALYTICS_NAV = [
  { id: 'overview', label: 'Overview', segment: '' },
  { id: 'contests', label: 'Contests', segment: 'contests' },
];

/** CodeChef drill-down — uses shared platform analytics framework. */
export const FACULTY_CODECHEF_ANALYTICS_NAV = [
  { id: 'overview', label: 'Overview', segment: '' },
  { id: 'students', label: 'Students', segment: 'students' },
  { id: 'contests', label: 'Contests', segment: 'contests' },
  { id: 'ratings', label: 'Ratings', segment: 'ratings' },
  { id: 'activity', label: 'Activity', segment: 'activity' },
];

/** Codeforces drill-down — kept separate from assignment analytics. */
export const FACULTY_CODEFORCES_ANALYTICS_NAV = [
  { id: 'overview', label: 'Overview', segment: '' },
  { id: 'students', label: 'Students', segment: 'students' },
  { id: 'contests', label: 'Contests', segment: 'contests' },
  { id: 'ratings', label: 'Ratings', segment: 'ratings' },
  { id: 'tags', label: 'Tags', segment: 'tags' },
  { id: 'trends', label: 'Trends', segment: 'trends' },
];

/** LeetCode drill-down — mirrors Codeforces tab structure. */
export const FACULTY_LEETCODE_ANALYTICS_NAV = [
  { id: 'overview', label: 'Overview', segment: '' },
  { id: 'students', label: 'Students', segment: 'students' },
  { id: 'trends', label: 'Trends', segment: 'trends' },
  { id: 'ratings', label: 'Ratings', segment: 'ratings' },
  { id: 'tags', label: 'Tags & Difficulty', segment: 'tags' },
  { id: 'contests', label: 'Contests', segment: 'contests' },
];

/** Flat list for legacy consumers — primary sections only. */
export const FACULTY_ANALYTICS_NAV = FACULTY_ANALYTICS_PRIMARY_NAV;

/** @deprecated Use {@link FACULTY_ANALYTICS_NAV}. */
export const CODING_ANALYTICS_NAV = FACULTY_ANALYTICS_NAV;

export const PLATFORM_LINKED_FIELD = {
  codechef: 'linkedStudents',
  geeksforgeeks: 'linkedStudents',
  codeforces: 'linkedAccounts',
  leetcode: 'linkedStudents',
};

export const PLATFORM_LABELS = {
  leetcode: 'LeetCode',
  codeforces: 'Codeforces',
  geeksforgeeks: 'GeeksforGeeks',
  codechef: 'CodeChef',
};

/** Maps legacy `overview` segment to `overall`. */
export function analyticsPath(segment) {
  const mapped = segment === 'overview' ? 'overall' : segment;
  return `${FACULTY_ANALYTICS_BASE}/${mapped}`;
}

/** Assignment analytics with optional category filter (e.g. critical). */
export function assignmentAnalyticsPath({ category } = {}) {
  const base = analyticsPath('batches');
  if (!category || category === 'all') return base;
  return `${base}?category=${encodeURIComponent(category)}`;
}

/** Platform analytics routes — overview hub or individual platform page. */
export function platformAnalyticsPath(platformSegment) {
  if (!platformSegment || platformSegment === 'comparison') {
    return `${FACULTY_ANALYTICS_BASE}/platforms`;
  }
  return `${FACULTY_ANALYTICS_BASE}/platforms/${platformSegment}`;
}

/** Platform analytics with optional batch context for deep links from batch workflows. */
export function platformAnalyticsPathWithBatch(platformSegment, batchId) {
  const base = platformAnalyticsPath(platformSegment);
  if (!batchId) return base;
  return `${base}?batchId=${encodeURIComponent(String(batchId))}`;
}

/** Codeforces platform analytics sub-routes. */
export function codeforcesAnalyticsPath(segment = '') {
  const base = `${FACULTY_ANALYTICS_BASE}/platforms/codeforces`;
  if (!segment) return base;
  return `${base}/${segment}`;
}

export function codeforcesSegmentFromPath(pathname) {
  const prefix = `${FACULTY_ANALYTICS_BASE}/platforms/codeforces`;
  if (!pathname.startsWith(prefix)) return '';
  const rest = pathname.slice(prefix.length).replace(/^\//, '');
  const segment = rest.split('/')[0] ?? '';
  return FACULTY_CODEFORCES_ANALYTICS_NAV.some((item) => item.segment === segment) ? segment : '';
}

/** CodeChef platform analytics sub-routes. */
export function codechefAnalyticsPath(segment = '') {
  const base = `${FACULTY_ANALYTICS_BASE}/platforms/codechef`;
  if (!segment) return base;
  return `${base}/${segment}`;
}

export function codechefSegmentFromPath(pathname) {
  const prefix = `${FACULTY_ANALYTICS_BASE}/platforms/codechef`;
  if (!pathname.startsWith(prefix)) return '';
  const rest = pathname.slice(prefix.length).replace(/^\//, '');
  const segment = rest.split('/')[0] ?? '';
  return FACULTY_CODECHEF_ANALYTICS_NAV.some((item) => item.segment === segment) ? segment : '';
}

/** Contest Analytics platform sub-routes. */
export function contestAnalyticsPath(segment = '') {
  const base = `${FACULTY_ANALYTICS_BASE}/platforms/contests`;
  if (!segment) return base;
  return `${base}/${segment}`;
}

export function contestAnalyticsSegmentFromPath(pathname) {
  const prefix = `${FACULTY_ANALYTICS_BASE}/platforms/contests`;
  if (!pathname.startsWith(prefix)) return '';
  const rest = pathname.slice(prefix.length).replace(/^\//, '');
  const segment = rest.split('/')[0] ?? '';
  return FACULTY_CONTEST_ANALYTICS_NAV.some((item) => item.segment === segment) ? segment : '';
}

/** LeetCode platform analytics sub-routes. */
export function leetcodeAnalyticsPath(segment = '') {
  const base = `${FACULTY_ANALYTICS_BASE}/platforms/leetcode`;
  if (!segment) return base;
  return `${base}/${segment}`;
}

export function leetcodeSegmentFromPath(pathname) {
  const prefix = `${FACULTY_ANALYTICS_BASE}/platforms/leetcode`;
  if (!pathname.startsWith(prefix)) return '';
  const rest = pathname.slice(prefix.length).replace(/^\//, '');
  const segment = rest.split('/')[0] ?? '';
  return FACULTY_LEETCODE_ANALYTICS_NAV.some((item) => item.segment === segment) ? segment : '';
}

/** @deprecated Use {@link analyticsPath}. */
export function codingAnalyticsPath(segment) {
  if (['leetcode', 'codeforces', 'geeksforgeeks', 'codechef'].includes(segment)) {
    return platformAnalyticsPath(segment);
  }
  return analyticsPath(segment);
}

/** Resolve primary tab segment from a pathname. */
export function analyticsPrimarySegmentFromPath(pathname) {
  const rest = pathname.replace(`${FACULTY_ANALYTICS_BASE}/`, '');
  const first = rest.split('/')[0];
  if (first === 'platforms') return 'platforms';
  const match = FACULTY_ANALYTICS_PRIMARY_NAV.find((item) => item.segment === first);
  return match?.segment ?? 'overall';
}

/** Resolve platform sub-tab segment from a pathname. */
export function platformSegmentFromPath(pathname) {
  if (!pathname.includes(`${FACULTY_ANALYTICS_BASE}/platforms`)) return 'comparison';
  const rest = pathname.replace(`${FACULTY_ANALYTICS_BASE}/platforms`, '').replace(/^\//, '');
  const segment = rest.split('/')[0];
  if (!segment) return 'comparison';
  return FACULTY_PLATFORM_ANALYTICS_NAV.some((item) => item.segment === segment)
    ? segment
    : 'comparison';
}

/** Legacy flat platform paths redirected to nested platform routes. */
export const LEGACY_PLATFORM_ANALYTICS_SEGMENTS = [
  'leetcode',
  'codeforces',
  'geeksforgeeks',
  'codechef',
];

export function getWorkloadStatus(total) {
  const n = Number(total) || 0;
  if (n <= 5) return { emoji: '🟢', label: 'Healthy', color: 'success.main' };
  if (n <= 15) return { emoji: '🟡', label: 'Watch', color: 'warning.main' };
  return { emoji: '🔴', label: 'Critical', color: 'error.main' };
}
